import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import moment from 'moment';
import AsyncSearch from 'components/AsyncSearch';
import useDebounce from 'hooks/useDebounce';
import {
  getPartnerRatingAreas,
  getPartnerRatingRealEstates,
  getPartnerRatingServiceOrgs,
  getServiceTypeListAllShowInRating,
} from '../../api';
import { IArea, IRealEstate, IServiceOrg, IServiceType } from '../../interfaces';
import { Points, Recommendations } from '../../components';
import { PartnersRatingReportByRealEstate } from './components';
import { useExportPartnerRatingByRealEstateExcelReport, useGetPartnerRatingByRealEstate } from './hooks';
import { OrderColumn } from './interfaces';

const ByRealEstate = () => {
  const [selectedServiceOrgs, setServiceOrgs] = useState<IServiceOrg[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<IArea[]>([]);
  const [selectedRealEstates, setSelectedRealEstates] = useState<IRealEstate[]>([]);
  const [selectedServiceTypes, setSelectedServiceType] = useState<IServiceType[]>([]);
  const [isActivePartner, setIsActivePartner] = useState(1);
  const [selectedYear, setSelectedYear] = useState(() => moment());
  const [orderColumn, setOrderColumn] = useState<OrderColumn>({ columnName: 'partnerName', sortType: 'ASC' });
  const [pageNo, setPageNo] = useState(1);
  const pageSize = 200;

  const debouncedServiceOrgs = useDebounce(selectedServiceOrgs);
  const debouncedAreas = useDebounce(selectedAreas);
  const debouncedRealEstates = useDebounce(selectedRealEstates);
  const debouncedServiceTypes = useDebounce(selectedServiceTypes);
  const debouncedYear = useDebounce(selectedYear);

  const { fetchPartnerRatingByRealEstate, ...partnersRatingState } = useGetPartnerRatingByRealEstate();
  const { exportExcelReport, loading: exportExcelLoading } = useExportPartnerRatingByRealEstateExcelReport();

  const params = {
    pageNo,
    pageSize,
    serviceOrgGuids: debouncedServiceOrgs.map((serviceOrg) => serviceOrg.serviceOrgGuid),
    areaIds: debouncedAreas.map((area) => area.areaId),
    realEstateGuids: debouncedRealEstates.map((realEstate) => realEstate.realEstateGuid),
    serviceTypeIds: debouncedServiceTypes.map((serviceType) => serviceType.serviceTypeId),
    year: selectedYear ? moment(debouncedYear).get('year') : null,
    orderColumn,
    isActivePartner: Boolean(isActivePartner),
  };
  const mapKey = JSON.stringify(
    Object.entries(params)
      .map(([, value]) => value)
      .filter(Boolean),
  );

  function handleChangeIsActivePartner(event: React.ChangeEvent<{ value: unknown }>) {
    setIsActivePartner(Number(event.target.value));
    setOrderColumn({ columnName: 'partnerName', sortType: 'ASC' });
  }

  function handleResetFilters() {
    setServiceOrgs([]);
    setSelectedAreas([]);
    setSelectedRealEstates([]);
    setSelectedServiceType([]);
    setIsActivePartner(1);
    setSelectedYear(moment());
  }

  useEffect(() => {
    fetchPartnerRatingByRealEstate({ params });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageNo,
    debouncedServiceOrgs,
    debouncedAreas,
    debouncedRealEstates,
    debouncedServiceTypes,
    debouncedYear,
    isActivePartner,
    orderColumn.columnName,
    orderColumn.sortType,
  ]);

  const partnerRating = partnersRatingState.data.get(mapKey) || { list: [], totalCount: 0 };

  return (
    <Box sx={{ bgcolor: '#fff', borderRadius: 20 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', p: 3, mb: 3, gridGap: 24 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gridGap: 8 }}>
          <Typography component="h2" variant="h5">
            <b>Рейтинг партнеров</b>
          </Typography>

          <Points />
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedServiceOrgs}
              fetch={getPartnerRatingServiceOrgs}
              uniqueBy="serviceOrgGuid"
              matchKeys={['currentName']}
              getOptionLabel={(option) => option.currentName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, options) => setServiceOrgs(options)}
              renderInput={(props) => <TextField {...props} label="Дирекции" variant="filled" size="small" />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedAreas}
              fetch={getPartnerRatingAreas}
              config={{
                params: { serviceOrgGuids: selectedServiceOrgs.map((serviceOrg) => serviceOrg.serviceOrgGuid) },
              }}
              uniqueBy="areaId"
              matchKeys={['areaName']}
              getOptionLabel={(option) => option.areaName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, options) => setSelectedAreas(options)}
              renderInput={(props) => <TextField {...props} label="Участки" variant="filled" size="small" />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedRealEstates}
              fetch={getPartnerRatingRealEstates}
              remoteSearch
              config={{ params: { areaIds: selectedAreas.map((area) => area.areaId) } }}
              uniqueBy="realEstateGuid"
              matchKeys={['realEstateName']}
              getOptionLabel={(option) => option.realEstateName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, option) => setSelectedRealEstates(option)}
              renderInput={(props) => (
                <TextField {...props} label="Объекты недвижимости" variant="filled" size="small" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <AsyncSearch
              multiple
              value={selectedServiceTypes}
              fetch={getServiceTypeListAllShowInRating}
              uniqueBy="serviceTypeId"
              matchKeys={['serviceTypeName']}
              getOptionLabel={(option) => option.serviceTypeName}
              limitTags={1}
              disabled={partnersRatingState.loading}
              onChange={(event, options) => setSelectedServiceType(options)}
              renderInput={(props) => <TextField {...props} label="Типы услуг" variant="filled" size="small" />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl variant="filled" size="small" disabled={partnersRatingState.loading} fullWidth>
              <InputLabel>Договор с партнером</InputLabel>

              <Select name="isActivePartner" value={isActivePartner} label="Тип" onChange={handleChangeIsActivePartner}>
                <MenuItem value={1}>Действующие</MenuItem>
                <MenuItem value={0}>Недействующие</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <Box sx={{ display: 'flex', gridGap: 16, alignItems: 'center' }}>
              <DatePicker
                variant="inline"
                inputVariant="filled"
                size="small"
                label="Год"
                format="yyyy"
                openTo="year"
                views={['year']}
                value={selectedYear}
                disabled={partnersRatingState.loading}
                onChange={(date) => date && setSelectedYear(moment(date))}
              />

              <Button variant="outlined" onClick={handleResetFilters}>
                Сбросить
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Recommendations />
      </Box>

      <PartnersRatingReportByRealEstate
        {...partnersRatingState}
        data={partnerRating}
        exportExcelLoading={exportExcelLoading}
        year={selectedYear.get('year')}
        page={pageNo}
        pageSize={pageSize}
        isActivePartner={Boolean(isActivePartner)}
        orderColumn={orderColumn}
        onChangeOrderColumn={setOrderColumn}
        onExportExcel={() => exportExcelReport({ params })}
        onChangePage={(page) => setPageNo(page)}
      />
    </Box>
  );
};

export default ByRealEstate;
